import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  Chip,
  Divider,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  ArrowBack,
  CheckCircle,
  Warning,
  HourglassEmpty,
  Cancel,
  Inventory2Outlined,
  CheckCircleOutline,
} from '@mui/icons-material';
import { api } from '../../services/api';
import { OrderStatus } from '../../types/api';
import { useNotification } from '../../contexts/NotificationContext';

interface OrderItem {
  id: number;
  item_id: number;
  item_name: string;
  category: string;
  unit: string;
  order_qty: number;
  pending_qty: number;
}

interface OrderDetail {
  id: number;
  direction: string;
  partner_id: number;
  partner_name: string;
  status: string;
  metadata?: Record<string, any>;
  created_by: number;
  creator_name: string;
  items: OrderItem[];
  created_at: string;
  updated_at: string;
}

const SupplierOrderView: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { alert: showAlert } = useNotification();

  const [order, setOrder] = useState<OrderDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [confirmError, setConfirmError] = useState<string | null>(null);

  const fetchOrder = () => {
    setLoading(true);
    api
      .get(`/api/orders/${id}`)
      .then((res) => {
        setOrder(res.data);
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchOrder();
  }, [id]);

  const handleConfirmReceipt = async () => {
    if (!order) return;
    setConfirmError(null);
    setConfirmLoading(true);
    try {
      await api.post('/api/orders/supplier/confirm', { order_id: order.id });
      await showAlert({
        title: 'Order Confirmed',
        message: `Order #${order.id} confirmed and inventory is now live.`,
        icon: 'success',
      });
      fetchOrder();
    } catch (err: any) {
      setConfirmError(err.message || 'Failed to confirm order');
    } finally {
      setConfirmLoading(false);
    }
  };

  const getStatusChip = (status: string) => {
    switch (status) {
      case OrderStatus.CREATED:
        return (
          <Chip
            icon={<Inventory2Outlined />}
            label="On Hold"
            color="warning"
            variant="outlined"
          />
        );
      case OrderStatus.COMPLETED:
        return <Chip icon={<CheckCircle />} label="Completed" color="success" />;
      case OrderStatus.PARTIAL_FULFILLED:
        return <Chip icon={<Warning />} label="Partial Fulfilled" color="warning" />;
      case OrderStatus.PENDING:
        return <Chip icon={<HourglassEmpty />} label="Pending" color="primary" />;
      case OrderStatus.CANCELLED:
        return <Chip icon={<Cancel />} label="Cancelled" color="error" />;
      default:
        return <Chip label={status} />;
    }
  };

  const canConfirm =
    order?.status === OrderStatus.CREATED &&
    (order?.direction === 'INWARD');

  if (loading) {
    return <Typography sx={{ p: 3 }}>Loading...</Typography>;
  }

  if (!order) {
    return <Typography sx={{ p: 3 }}>Order not found</Typography>;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)} sx={{ mb: 2 }}>
        Back to Orders
      </Button>

      <Paper sx={{ p: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="h5" fontWeight="bold">
            Order #{order.id}
          </Typography>

          {canConfirm && (
            <Button
              variant="contained"
              color="success"
              startIcon={confirmLoading ? <CircularProgress size={16} color="inherit" /> : <CheckCircleOutline />}
              onClick={handleConfirmReceipt}
              disabled={confirmLoading}
            >
              {confirmLoading ? 'Confirming...' : 'Confirm Order'}
            </Button>
          )}
        </Box>

        {canConfirm && (
          <Alert severity="info" sx={{ mb: 2 }}>
            This order is on hold. Click <strong>Confirm Order</strong> to make inventory live.
          </Alert>
        )}

        {confirmError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setConfirmError(null)}>
            {confirmError}
          </Alert>
        )}

        <Divider sx={{ my: 2 }} />

        {/* Order Info */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: 2,
            mb: 3,
          }}
        >
          <Box>
            <Typography variant="body2" color="text.secondary">
              Supplier Name
            </Typography>
            <Typography variant="h6">{order.partner_name || 'N/A'}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">
              Status
            </Typography>
            <Box sx={{ mt: 0.5 }}>{getStatusChip(order.status)}</Box>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">
              Direction
            </Typography>
            <Typography variant="body1">{order.direction}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">
              Created At
            </Typography>
            <Typography variant="body1">
              {new Date(order.created_at).toLocaleString()}
            </Typography>
          </Box>
        </Box>

        {order.metadata?.note && (
          <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Remark / Note
            </Typography>
            <Typography variant="body1" sx={{ fontStyle: 'italic' }}>
              {order.metadata.note}
            </Typography>
          </Box>
        )}

        {order.metadata?.transport_provider && (
          <Box sx={{ mb: 3, p: 2, bgcolor: '#e3f2fd', borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Transport Preference
            </Typography>
            <Typography variant="body1" fontWeight="medium">
              {order.metadata.transport_provider}
            </Typography>
          </Box>
        )}

        <Divider sx={{ my: 2 }} />

        {/* Items Table */}
        <Typography variant="h6" gutterBottom>
          Order Items
        </Typography>
        <Table>
          <TableHead sx={{ bgcolor: '#f5f5f5' }}>
            <TableRow>
              <TableCell>Item Name</TableCell>
              <TableCell>Category</TableCell>
              <TableCell align="center">Order Qty</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {order.items &&
              order.items.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>{item.item_name}</TableCell>
                  <TableCell>{item.category}</TableCell>
                  <TableCell align="center">{item.order_qty}</TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
};

export default SupplierOrderView;
